import { ExclamationCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, message, Modal, Upload } from 'antd';
import type { RcFile, UploadChangeParam, UploadFile } from 'antd/es/upload';
import { Loader } from 'components';
import { useUploadBulkExcelCoupon } from 'features/shop-item/use-cases/coupon-bulk-upload';
import { useState } from 'react';
import * as XLSX from 'xlsx';

interface EventCouponBulkUploadProps {
    id: any;
}

export const EventCouponBulkUpload = ({
    id,
}: EventCouponBulkUploadProps) => {
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [loading, setLoading] = useState(false);

    const { mutate } = useUploadBulkExcelCoupon();

    const labels = [
        'Coupon Title',
        'Limit Usage',
        'Value Type',
        'Value',
        'Start Date',
        'End Date',
        // 'Category',
        // 'Coupon Code',
        'Multiple Use',
    ];

    /**
     * Normalize Excel header.
     *
     * Example:
     * "Coupon Title" -> "coupontitle"
     * " Start Date " -> "startdate"
     */
    const normalizeHeader = (value: any): string => {
        return String(value ?? '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '');
    };

 
    const isEmptyRow = (row: any[]): boolean => {
        if (!row || row.length === 0) {
            return true;
        }

        return row.every(
            (cell) =>
                cell === undefined ||
                cell === null ||
                String(cell).trim() === '',
        );
    };

    const isEmptyValue = (value: any): boolean => {
        return (
            value === undefined ||
            value === null ||
            String(value).trim() === ''
        );
    };

   
    const validateNumber = (input: any): boolean => {
        if (isEmptyValue(input)) {
            return false;
        }

        const value = Number(input);

        return !Number.isNaN(value) && Number.isFinite(value);
    };

   
    const convertExcelDateToJSDate = (
        excelDate: number,
    ): Date | null => {
        if (!Number.isFinite(excelDate)) {
            return null;
        }

        const excelEpoch = new Date(Date.UTC(1899, 11, 30));

        const date = new Date(
            excelEpoch.getTime() +
                Math.round(excelDate * 24 * 60 * 60 * 1000),
        );

        return Number.isNaN(date.getTime()) ? null : date;
    };

   
    const parseExcelDate = (value: any): Date | null => {
        if (isEmptyValue(value)) {
            return null;
        }

        // Excel serial number
        if (typeof value === 'number') {
            return convertExcelDateToJSDate(value);
        }

        const stringValue = String(value).trim();

        // DD-MM-YYYY
        const ddMmYyyyMatch = stringValue.match(
            /^(\d{1,2})-(\d{1,2})-(\d{4})$/,
        );

        if (ddMmYyyyMatch) {
            const day = Number(ddMmYyyyMatch[1]);
            const month = Number(ddMmYyyyMatch[2]);
            const year = Number(ddMmYyyyMatch[3]);

            const date = new Date(year, month - 1, day);

            if (
                date.getFullYear() !== year ||
                date.getMonth() !== month - 1 ||
                date.getDate() !== day
            ) {
                return null;
            }

            return date;
        }

        // DD/MM/YYYY
        const slashDateMatch = stringValue.match(
            /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
        );

        if (slashDateMatch) {
            const day = Number(slashDateMatch[1]);
            const month = Number(slashDateMatch[2]);
            const year = Number(slashDateMatch[3]);

            const date = new Date(year, month - 1, day);

            if (
                date.getFullYear() !== year ||
                date.getMonth() !== month - 1 ||
                date.getDate() !== day
            ) {
                return null;
            }

            return date;
        }

        // YYYY-MM-DD
        const yyyyMmDdMatch = stringValue.match(
            /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
        );

        if (yyyyMmDdMatch) {
            const year = Number(yyyyMmDdMatch[1]);
            const month = Number(yyyyMmDdMatch[2]);
            const day = Number(yyyyMmDdMatch[3]);

            const date = new Date(year, month - 1, day);

            if (
                date.getFullYear() !== year ||
                date.getMonth() !== month - 1 ||
                date.getDate() !== day
            ) {
                return null;
            }

            return date;
        }

        // Fallback
        const date = new Date(stringValue);

        if (Number.isNaN(date.getTime())) {
            return null;
        }

        return date;
    };

   
    const formatDateForError = (value: any): string => {
        if (isEmptyValue(value)) {
            return 'empty';
        }

        if (typeof value === 'number') {
            const date = convertExcelDateToJSDate(value);

            return date ? date.toLocaleDateString() : String(value);
        }

        return String(value);
    };

  
    const showValidationErrors = (errors: string[]) => {
        Modal.error({
            title: 'Validation Errors',
            icon: <ExclamationCircleOutlined />,
            okText: 'OK',
            width: 650,
            content: (
                <div
                    style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                    }}
                >
                    <ul style={{ paddingLeft: '20px' }}>
                        {errors.map((error, index) => (
                            <li key={index} style={{ marginBottom: '6px' }}>
                                <b>{error}</b>
                            </li>
                        ))}
                    </ul>
                </div>
            ),
        });
    };

    
    const handleUpload = (file: RcFile) => {
        const isExcel =
            file.type === 'application/vnd.ms-excel' ||
            file.type ===
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            /\.(xls|xlsx)$/i.test(file.name);

        if (!isExcel) {
            message.error('You can only upload Excel files!');
            
return Upload.LIST_IGNORE;
        }

        setLoading(true);
        setFileList([]);

        const reader = new FileReader();

        reader.onload = (event: ProgressEvent<FileReader>) => {
            try {
                const data = event.target?.result;

                if (!data) {
                    setLoading(false);
                    message.error('Unable to read the Excel file.');
                    
return;
                }

                const workbook = XLSX.read(data, {
                    type: 'binary',
                    cellDates: true,
                });

                if (!workbook.SheetNames.length) {
                    setLoading(false);
                    message.error('The Excel file contains no sheets.');
                    
return;
                }

                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];

                const jsonData = XLSX.utils.sheet_to_json<any[]>(sheet, {
                    header: 1,
                    blankrows: false,
                    defval: '',
                });

                
                if (jsonData.length <= 1) {
                    setLoading(false);
                    message.error(
                        'The Excel file does not contain any data.',
                    );
                    
return;
                }

              
                const header = jsonData[0] as any[];

                const lowerCaseHeader = header.map(normalizeHeader);

                const lowerCaseRequiredColumns = labels.map(
                    normalizeHeader,
                );

               

                
                const missingColumns =
                    lowerCaseRequiredColumns.filter(
                        (column) =>
                            !lowerCaseHeader.includes(column),
                    );

             
                const extraColumns = lowerCaseHeader.filter(
                    (column) =>
                        column !== '' &&
                        !lowerCaseRequiredColumns.includes(
                            column,
                        ),
                );

                const errors: string[] = [];

                
                if (missingColumns.length > 0) {
                    missingColumns.forEach((column) => {
                        const index =
                            lowerCaseRequiredColumns.indexOf(
                                column,
                            );

                        if (index !== -1) {
                            errors.push(
                                `Missing required column: "${labels[index]}".`,
                            );
                        }
                    });
                }

                
                if (extraColumns.length > 0) {
                    const extraColumnNames = extraColumns
                        .map((column) => {
                            const index =
                                lowerCaseHeader.indexOf(
                                    column,
                                );

                            return header[index];
                        })
                        .join(', ');

                    errors.push(
                        `Extra columns found: "${extraColumnNames}". Please remove them.`,
                    );
                }

                
                if (missingColumns.length > 0) {
                    setLoading(false);
                    showValidationErrors(errors);
                    
return;
                }

                // const categoryIndex =
                //     lowerCaseHeader.indexOf('category');

                const couponTitleIndex =
                    lowerCaseHeader.indexOf('coupontitle');

                // const couponCodeIndex =
                //     lowerCaseHeader.indexOf('couponcode');

                const multipleUseIndex =
                    lowerCaseHeader.indexOf('multipleuse');

                const limitUsageIndex =
                    lowerCaseHeader.indexOf('limitusage');

                const valueTypeIndex =
                    lowerCaseHeader.indexOf('valuetype');

                const valueIndex =
                    lowerCaseHeader.indexOf('value');

                const startDateIndex =
                    lowerCaseHeader.indexOf('startdate');

                const endDateIndex =
                    lowerCaseHeader.indexOf('enddate');

              
                const dataRows = jsonData
                    .slice(1)
                    .map((row, index) => ({
                        row,
                        excelRowNumber: index + 2,
                    }))
                    .filter(
                        ({ row }) => !isEmptyRow(row),
                    );

                if (dataRows.length === 0) {
                    setLoading(false);
                    message.error(
                        'The Excel file does not contain any data rows.',
                    );
                    
return;
                }

                dataRows.forEach(
                    ({ row, excelRowNumber }) => {
                        
                        // if (
                        //     categoryIndex !== -1 &&
                        //     isEmptyValue(
                        //         row[categoryIndex],
                        //     )
                        // ) {
                        //     errors.push(
                        //         `Category is missing in row ${excelRowNumber}.`,
                        //     );
                        // }

                      
                        if (
                            couponTitleIndex !== -1 &&
                            isEmptyValue(
                                row[couponTitleIndex],
                            )
                        ) {
                            errors.push(
                                `Coupon Title is missing in row ${excelRowNumber}.`,
                            );
                        }

                        // if (
                        //     couponCodeIndex !== -1 &&
                        //     isEmptyValue(
                        //         row[couponCodeIndex],
                        //     )
                        // ) {
                        //     errors.push(
                        //         `Coupon Code is missing in row ${excelRowNumber}.`,
                        //     );
                        // }

                       
                        if (
                            limitUsageIndex !== -1 &&
                            !validateNumber(
                                row[limitUsageIndex],
                            )
                        ) {
                            errors.push(
                                `Limit Usage must be a number in row ${excelRowNumber}.`,
                            );
                        }

                      
                        if (
                            multipleUseIndex !== -1 &&
                            isEmptyValue(
                                row[multipleUseIndex],
                            )
                        ) {
                            errors.push(
                                `Multiple Use is missing in row ${excelRowNumber}.`,
                            );
                        } else if (
                            multipleUseIndex !== -1
                        ) {
                            const multipleUse = String(
                                row[multipleUseIndex],
                            )
                                .trim()
                                .toLowerCase();

                            if (
                                ![
                                    'yes',
                                    'no',
                                    'true',
                                    'false',
                                ].includes(multipleUse)
                            ) {
                                errors.push(
                                    `Multiple Use must be Yes/No in row ${excelRowNumber}.`,
                                );
                            }
                        }

                       
                        if (
                            valueTypeIndex !== -1 &&
                            isEmptyValue(
                                row[valueTypeIndex],
                            )
                        ) {
                            errors.push(
                                `Value Type is missing in row ${excelRowNumber}.`,
                            );
                        } else if (
                            valueTypeIndex !== -1
                        ) {
                            const valueType = String(
                                row[valueTypeIndex],
                            )
                                .trim()
                                .toLowerCase();

                            if (
                                ![
                                    'flat',
                                    'percent',
                                ].includes(valueType)
                            ) {
                                errors.push(
                                    `Value Type must be Flat or Percent in row ${excelRowNumber}.`,
                                );
                            }
                        }

                        /**
                         * Value
                         */
                        if (
                            valueIndex !== -1 &&
                            !validateNumber(
                                row[valueIndex],
                            )
                        ) {
                            errors.push(
                                `Value must be a number in row ${excelRowNumber}.`,
                            );
                        }

                        /**
                         * Start Date
                         */
                        let startDate: Date | null =
                            null;

                        if (startDateIndex !== -1) {
                            const startDateValue =
                                row[startDateIndex];

                            if (
                                isEmptyValue(
                                    startDateValue,
                                )
                            ) {
                                errors.push(
                                    `Start Date is missing in row ${excelRowNumber}.`,
                                );
                            } else {
                                startDate =
                                    parseExcelDate(
                                        startDateValue,
                                    );

                                if (!startDate) {
                                    errors.push(
                                        `Invalid Start Date in row ${excelRowNumber}: ${formatDateForError(
                                            startDateValue,
                                        )}`,
                                    );
                                }
                            }
                        }

                        /**
                         * End Date
                         */
                        let endDate: Date | null = null;

                        if (endDateIndex !== -1) {
                            const endDateValue =
                                row[endDateIndex];

                            if (
                                isEmptyValue(
                                    endDateValue,
                                )
                            ) {
                                errors.push(
                                    `End Date is missing in row ${excelRowNumber}.`,
                                );
                            } else {
                                endDate =
                                    parseExcelDate(
                                        endDateValue,
                                    );

                                if (!endDate) {
                                    errors.push(
                                        `Invalid End Date in row ${excelRowNumber}: ${formatDateForError(
                                            endDateValue,
                                        )}`,
                                    );
                                }
                            }
                        }

                        /**
                         * End Date must be greater than Start Date.
                         */
                        if (
                            startDate &&
                            endDate &&
                            startDate >= endDate
                        ) {
                            errors.push(
                                `End Date must be greater than Start Date in row ${excelRowNumber}.`,
                            );
                        }
                    },
                );

                setLoading(false);

                
                if (errors.length > 0) {
                    showValidationErrors(errors);
                    
return;
                }

                handlePostExcel(file);
            } catch (error) {
                console.error(
                    'Excel validation error:',
                    error,
                );

                setLoading(false);

                message.error(
                    'Something went wrong while reading the Excel file.',
                );
            }
        };
        reader.onerror = () => {
            setLoading(false);

            message.error(
                'Failed to read the Excel file.',
            );
        };

        reader.readAsBinaryString(file);

    
        return false;
    };

    
    const handlePostExcel = (file: RcFile) => {
        const formData = new FormData();

        formData.append('EventId', String(id));
        formData.append('File', file);
        formData.append('Flag', 'manual');

        setLoading(true);

        mutate(formData, {
            onSuccess: () => {
                setLoading(false);
                setFileList([]);

                // message.success(
                //     `${file.name} uploaded successfully.`,
                // );
            },

            onError: (error: any) => {
                setLoading(false);
                setFileList([]);

                console.error(
                    'Bulk coupon upload error:',
                    error,
                );

                message.error(
                    error?.message ||
                        'Failed to upload Excel file.',
                );
            },
        });
    };

   
    const handleChange = (
        info: UploadChangeParam<UploadFile>,
    ) => {
       
        const newFileList = info.fileList.slice(-1);

        setFileList(newFileList);

        if (info.file.status === 'removed') {
            setFileList([]);
        }
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <div>
            <Upload
                beforeUpload={handleUpload}
                accept=".xls,.xlsx"
                multiple={false}
                showUploadList={true}
                fileList={fileList}
                onChange={handleChange}
            >
                <Button
                    icon={<UploadOutlined />}
                    style={{
                        marginRight: '18px',
                    }}
                    className="btn_common"
                >
                    Bulk Upload
                </Button>
            </Upload>
        </div>
    );
};